import { useEffect } from 'react'
import { startGameLoop } from './engine/gameLoop'
import { startAutosave, useGame } from './state/store'
import { Header } from './components/Header'
import { TapPad } from './components/TapPad'
import { LetterShop } from './components/LetterShop'
import { WordCraft } from './components/WordCraft'
import { Lexicon } from './components/Lexicon'
import { OfflineModal } from './components/OfflineModal'
import { ForgeMetrics } from './components/ForgeMetrics'
import { StoryPanel } from './components/StoryPanel'
import { AlphabetPanel } from './components/AlphabetPanel'
import { Board } from './components/Board'
import { OperatorBar } from './components/OperatorBar'
import { TypefaceTree } from './components/TypefaceTree'
import { PublishButton } from './components/PublishButton'
import { ToastFeed } from './components/Toast'
import { useKeyboardShortcuts } from './components/useKeyboardShortcuts'
import { ChallengePanel } from './components/ChallengePanel'
import { getThemeForEdition } from './config/themes'
import { GLOBAL } from './config/global'

export default function App() {
  useEffect(() => {
    const stopLoop = startGameLoop()
    const stopSave = startAutosave()
    return () => {
      stopLoop()
      stopSave()
    }
  }, [])

  useKeyboardShortcuts()

  const distinct = useGame((s) => s.distinct())
  const edition = useGame((s) => s.edition)
  const lexiconLen = useGame((s) => s.lexicon.length)
  const theme = getThemeForEdition(edition)

  const themeVars = {
    '--color-bg': theme.colors.bg,
    '--color-panel': theme.colors.panel,
    '--color-border': theme.colors.border,
    '--color-teal': theme.colors.teal,
    '--color-tealdim': theme.colors.tealdim,
    '--color-text': theme.colors.text,
    '--color-muted': theme.colors.muted,
    '--color-accent': theme.colors.accent,
    '--color-glow': theme.colors.glow,
    '--bg-gradient': theme.colors.bgGradient,
  } as React.CSSProperties

  return (
    <div style={themeVars} className="min-h-full mx-auto max-w-5xl px-4 pb-24">
      <Header />
      {lexiconLen > 0 && <ForgeMetrics />}
      {distinct > 0 && <Board />}
      <main className="mt-6 grid gap-6 md:grid-cols-[260px_1fr] items-start">
        <section className="sticky top-4 space-y-4">
          <TapPad />
          {distinct >= 4 ? (
            <ChallengePanel />
          ) : (
            <div className="rounded-lg border border-ink-border bg-ink-panel p-3">
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="text-ink-muted">
                  Letters toward Wordsmith
                </span>
                <span className="text-ink-teal">{distinct} / 4</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-ink-border">
                <div
                  className="h-full rounded-full bg-ink-teal transition-all duration-500"
                  style={{ width: `${Math.min(distinct / 4, 1) * 100}%` }}
                />
              </div>
              <p className="mt-1.5 text-[10px] text-ink-muted">
                Buy letters in frequency order. Passive Ink/s at Lv{GLOBAL.passiveUnlockLevel}.
              </p>
            </div>
          )}
          <AlphabetPanel />
        </section>
        <div className="space-y-6">
          <LetterShop />
          {edition >= 1 && <OperatorBar />}
          {distinct >= 4 ? (
            <>
              <WordCraft />
              <Lexicon />
              <StoryPanel />
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-ink-border bg-ink-panel p-4 text-center text-xs text-ink-muted">
              Own <span className="text-ink-teal">4 distinct letters</span> to
              unlock <span className="text-ink-text">Wordsmith</span> ({distinct}/4)
            </div>
          )}
          <TypefaceTree />
          <PublishButton />
        </div>
      </main>
      <OfflineModal />
      <ToastFeed />
    </div>
  )
}
